const STORAGE_PREFIX = 'schedule-employee-row-order';

export function getScheduleEmployeeOrderStorageKey(
	workplaceId: number | string,
	userId: number | string
): string {
	return `${STORAGE_PREFIX}:${userId}:${workplaceId}`;
}

function getLegacyScheduleEmployeeOrderStorageKey(workplaceId: number | string): string {
	return `${STORAGE_PREFIX}:${workplaceId}`;
}

function parseStoredOrder(raw: string | null): string[] | null {
	if (!raw) return null;

	try {
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed.map(String) : null;
	} catch {
		return null;
	}
}

export function getScheduleEmployeeOrder(
	workplaceId: number | string,
	userId: number | string
): string[] | null {
	try {
		const storageKey = getScheduleEmployeeOrderStorageKey(workplaceId, userId);
		const savedOrder = parseStoredOrder(localStorage.getItem(storageKey));
		if (savedOrder) return savedOrder;

		// One-time migration from workplace-only keys to per-user keys.
		const legacyOrder = parseStoredOrder(
			localStorage.getItem(getLegacyScheduleEmployeeOrderStorageKey(workplaceId))
		);
		if (legacyOrder) {
			localStorage.setItem(storageKey, JSON.stringify(legacyOrder));
		}

		return legacyOrder;
	} catch {
		return null;
	}
}

export function saveScheduleEmployeeOrder(
	workplaceId: number | string,
	userId: number | string,
	order: string[]
): void {
	try {
		localStorage.setItem(
			getScheduleEmployeeOrderStorageKey(workplaceId, userId),
			JSON.stringify(order)
		);
	} catch {
		// Ignore storage quota / privacy mode errors.
	}
}

type OrderableResource = {
	id: number | string;
	isOpenShift?: boolean;
	displayName?: string;
	name: string;
};

export function applyScheduleEmployeeOrder<T extends OrderableResource>(
	resources: T[],
	savedOrder: string[] | null
): Array<T & { sortOrder: number }> {
	const openShifts = resources.filter((resource) => resource.isOpenShift);
	const employees = resources.filter((resource) => !resource.isOpenShift);

	const employeeIds = new Set(employees.map((resource) => String(resource.id)));
	const orderedIds: string[] = [];

	if (savedOrder) {
		for (const id of savedOrder) {
			if (employeeIds.has(id) && !orderedIds.includes(id)) {
				orderedIds.push(id);
			}
		}
	}

	const remainingIds = employees
		.map((resource) => String(resource.id))
		.filter((id) => !orderedIds.includes(id))
		.sort((a, b) => {
			const resourceA = employees.find((resource) => String(resource.id) === a);
			const resourceB = employees.find((resource) => String(resource.id) === b);
			const nameA = resourceA?.displayName || resourceA?.name || '';
			const nameB = resourceB?.displayName || resourceB?.name || '';
			return nameA.localeCompare(nameB);
		});

	orderedIds.push(...remainingIds);

	// House Shifts always stay pinned as the first row(s).
	const orderedOpenShifts = openShifts.map((resource, index) => ({
		...resource,
		sortOrder: index,
	}));

	const employeeStart = orderedOpenShifts.length;
	const orderedEmployees = orderedIds.map((id, index) => {
		const resource = employees.find((entry) => String(entry.id) === id)!;
		return { ...resource, sortOrder: employeeStart + index };
	});

	return [...orderedOpenShifts, ...orderedEmployees];
}

export function reorderScheduleEmployeeIds(
	currentOrder: string[],
	draggedId: string,
	targetId: string
): string[] {
	if (draggedId === targetId) return currentOrder;

	const nextOrder = currentOrder.filter((id) => id !== draggedId);
	const targetIndex = nextOrder.indexOf(targetId);
	if (targetIndex === -1) return currentOrder;

	nextOrder.splice(targetIndex, 0, draggedId);
	return nextOrder;
}

export function getEmployeeOrderIds<T extends OrderableResource>(
	resources: T[],
	savedOrder: string[] | null
): string[] {
	return applyScheduleEmployeeOrder(resources, savedOrder)
		.filter((resource) => !resource.isOpenShift)
		.map((resource) => String(resource.id));
}
